import {exhaustive} from '@freckle/exhaustive'
import {fromMaybe} from '@freckle/maybe'
import {HttpError} from '@freckle/educator-entities/ts/common/exceptions/http-error'
import {addMaybeClassName} from '@freckle/educator-entities/ts/common/helpers/classnames'
import {useAsync} from '@freckle/educator-entities/ts/common/hooks/use-async'
import {Label} from '@freckle/educator-materials/ts/components/label'
import type {DropdownOptionsT} from '@freckle/educator-materials/ts/logic/dropdown'
import {flatOptions} from '@freckle/educator-materials/ts/logic/dropdown'
import {Dropdown} from '../dropdown'
import {teacherSelector} from './self-contained-teacher-selector.module.scss'

type ComponentLabelsT = {
  formLabel: string | null
  loadingText: string
}

export type FetchedOrBeFetchedT<T> =
  | {
      tag: 'fetched'
      value: T
    }
  | {
      tag: 'be-fetched'
      fetch: () => Promise<T>
    }

function comparableValues<T>(fetch: FetchedOrBeFetchedT<T>) {
  switch (fetch.tag) {
    case 'fetched':
      return fetch
    case 'be-fetched':
      return {...fetch, fetch: undefined}
    default:
      exhaustive(fetch)
  }
}

export type Props<TItem, TItemKey> = {
  id: string
  fetchItems: FetchedOrBeFetchedT<Array<TItem>>
  itemsToCollection: (a: Array<TItem>) => DropdownOptionsT<string>
  selectedItem: TItemKey
  onChangeSelection: (a: TItemKey) => Promise<unknown>
  labels: ComponentLabelsT
  dataTest?: string
  selectorClassName?: string | null
  onFetchError: ((error: Error) => void) | undefined | null
}

// Like `GenericSelector` but with the ability to fetch and update data
export function SelfContainedTeacherSelector<TItem, TItemKey>({
  id,
  labels: {formLabel, loadingText},
  fetchItems,
  onChangeSelection,
  itemsToCollection,
  selectedItem,
  dataTest,
  selectorClassName,
  onFetchError,
}: Props<TItem, TItemKey>) {
  const items = useAsync(
    () => {
      try {
        switch (fetchItems.tag) {
          case 'fetched':
            return Promise.resolve(fetchItems.value)
          case 'be-fetched':
            return fetchItems.fetch()
          default:
            exhaustive(fetchItems, 'FetchedOrBeFetchedT')
        }
      } catch (e: unknown) {
        if (onFetchError !== null && onFetchError !== undefined) {
          onFetchError(new HttpError(e))
        }
        throw e
      }
    },
    'fetchItemsForTeacherSelector',
    [id, comparableValues(fetchItems)],
  )

  const loading = items.data === undefined

  if (loading) {
    return (
      <Dropdown
        id={id}
        isSearchable={false}
        isClearable={false}
        dataTest={dataTest}
        dropdownClass={addMaybeClassName(teacherSelector, selectorClassName)}
        handleChange={() => {}}
        options={flatOptions([
          {value: null, label: loadingText, dataTest: 'math-system-loading-option'},
        ])}
        value={{value: null, label: loadingText}}
        isDisabled={loading}
        formatOptionLabel={option => <div data-test={option.dataTest}>{option.label}</div>}
      />
    )
  }

  const options = itemsToCollection(items.data)
  function getDropdownValue(selectedItem: TItemKey) {
    switch (options.tag) {
      case 'grouped':
        return fromMaybe(
          () => null,
          options.groups
            .flatMap(({options}) => options)
            .find(option => option.value === selectedItem),
        )
      case 'flat':
        return fromMaybe(
          () => null,
          options.options.find(option => option.value === selectedItem),
        )
      default:
        exhaustive((options as {tag: never}).tag, 'DropdownOptionsT')
    }
  }

  return (
    <>
      {formLabel !== null ? <Label htmlFor={id}>{formLabel}</Label> : null}
      {
        <Dropdown
          id={id}
          dropdownClass={addMaybeClassName(teacherSelector, selectorClassName)}
          dataTest={dataTest}
          options={options}
          value={getDropdownValue(selectedItem)}
          handleChange={value => onChangeSelection(value?.value as TItemKey)}
          isSearchable={false}
          isClearable={false}
          isDisabled={loading}
          formatOptionLabel={option => <div data-test={option.dataTest}>{option.label}</div>}
        />
      }
    </>
  )
}
